/**
 * Desktop megamenu disclosure semantics.
 *
 * The caret is a real disclosure button — rendered by
 * `snippets/header-megamenu-block.liquid` as
 * `<button type="button" class="x-icon-caret header-caret-toggle">` with a
 * static `aria-expanded="false"`, `aria-controls` on its `#menu-N` panel and an
 * accessible name from the visible menu label. Focus no longer auto-opens a
 * panel; it only applies the `is-focus` styling class. This file binds the
 * buttons, keeps `aria-expanded` truthful, and adds Escape-to-dismiss
 * (WCAG 2.2 SC 1.4.13 — dismissible without moving focus).
 *
 * Behavior lives here rather than in inline Alpine handlers because the markup
 * has 8 near-identical menu-type branches: mirroring state once in JS covers
 * every branch, including merchant blocks added later.
 */
(() => {
  'use strict';

  const OPEN_CLASS = 'is-focus';
  const TOGGLE_SELECTOR = '.header-caret-toggle';
  const BOUND_FLAG = 'a11yMenuBound';

  /** Looked up lazily: vendors.js (Alpine) loads after this file. */
  const menuStore = () =>
    (window.Alpine && window.Alpine.store && window.Alpine.store('xHeaderMenu')) || null;

  /**
   * The nav item a caret toggle belongs to. `.has-dropdown` is the hook
   * `theme.js` itself keys off in `selectItem()` / `clickItem()`, and the only
   * class shared by all 8 menu-type branches.
   */
  const itemFor = (toggle) => toggle.closest('.has-dropdown');

  /**
   * ⚠ Panel visibility is NOT the `is-focus` class, which is styling only. The
   * panel is a `.toggle-menu` shown by removing `toggle-menu-hidden`, managed by
   * `xHeaderMenu.selectItem()` / `hideMenu()` in theme.js.
   */
  const isOpen = (item) => {
    const panel = item && item.querySelector('.toggle-menu');
    return !!panel && !panel.classList.contains('toggle-menu-hidden');
  };

  const syncToggle = (toggle) => {
    const item = itemFor(toggle);
    if (!item) return;
    toggle.setAttribute('aria-expanded', isOpen(item) ? 'true' : 'false');
  };

  const syncAll = (root) => {
    root.querySelectorAll(TOGGLE_SELECTOR).forEach(syncToggle);
  };

  /**
   * Open and close through the theme's own paths — `clickItem()` and
   * `hideMenu()`, the latter shared with the nav's `click.away`.
   *
   * `clickItem()` decides direction from the `clicked` class, which the hover
   * path (`selectItem()`) can leave on a different item without clearing.
   * Deciding direction from the panel's real visibility and clearing `clicked`
   * before opening keeps the button honest in both `select_motion` modes.
   */
  const toggleItem = (toggle, event) => {
    const item = itemFor(toggle);
    const menu = menuStore();
    if (!item || !menu) return;

    if (isOpen(item)) {
      menu.hideMenu(item);
    } else {
      item.classList.remove('clicked');
      menu.clickItem(item, event, false, false, false);
    }

    syncToggle(toggle);
  };

  const bindToggles = (root) => {
    root.querySelectorAll(TOGGLE_SELECTOR).forEach((toggle) => {
      if (toggle.dataset[BOUND_FLAG]) return;
      toggle.dataset[BOUND_FLAG] = 'true';

      // Bound on the button itself, so `stopPropagation` runs before the
      // wrapper's inline `@click` (select_motion: click) toggles the same panel
      // a second time.
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleItem(toggle, event);
      });
    });
  };

  const initWithin = (root) => {
    bindToggles(root);
    syncAll(root);
  };

  const start = () => {
    const headers = document.querySelectorAll('header, #sticky-header-content');
    if (!headers.length) return;

    headers.forEach((header) => {
      initWithin(header);

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length) {
            // Panels render from <template x-if> / x-teleport, so items and
            // their toggles appear after Alpine init.
            initWithin(header);
          } else if (mutation.type === 'attributes') {
            // A panel gaining or losing `toggle-menu-hidden` is what changes
            // the expanded state. Re-syncing all of a header's toggles is
            // cheaper than working out which item the mutation belongs to,
            // and `attributeFilter` keeps our own aria writes from looping.
            syncAll(header);
          }
        }
      });

      observer.observe(header, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class']
      });
    });

    /**
     * Escape dismisses an open megamenu without moving focus (WCAG 2.2 SC
     * 1.4.13). Delegates to `xHeaderMenu.hideMenu()`, which hides every panel,
     * clears `.clicked` and updates the backdrop overlay; it ignores its
     * argument unless isSub.
     *
     * Deliberately does not also suppress the theme's inline mouseover
     * handlers — see the known gap in docs/accessibility/remediation.md.
     */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!document.querySelector('.toggle-menu:not(.toggle-menu-hidden)')) return;

      const menu = menuStore();
      if (!menu || typeof menu.hideMenu !== 'function') return;

      menu.hideMenu(null);

      // Reset the styling state so the caret un-rotates.
      document.querySelectorAll(`.${OPEN_CLASS}`).forEach((el) => {
        el.classList.remove(OPEN_CLASS);
      });

      headers.forEach(syncAll);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
